import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { useCustomerSkus } from '@/api/generated';
import { SkuOverviewCard } from '@/components/blocks/shelf-detail/sku-overview/sku-overview-card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';

interface ShelfDetailSkuSearchProps {
  onSelectSku: (skuMetadataId: string) => void;
  confirmedSkuMetadataId?: string;
  className?: string;
  customerId: number;
}

export const SkuOverview: FC<ShelfDetailSkuSearchProps> = ({
  onSelectSku,
  confirmedSkuMetadataId,
  customerId,
  className,
}: ShelfDetailSkuSearchProps) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const hasQuery = useMemo(
    () => debouncedQuery.trim().length > 0,
    [debouncedQuery]
  );
  const { data, isLoading } = useCustomerSkus(
    customerId,
    { query: debouncedQuery },
    { query: { enabled: hasQuery } }
  );
  const isSearchingSkus = useMemo(
    () => isLoading && hasQuery,
    [isLoading, hasQuery]
  );
  const items = useMemo(() => data?.items ?? [], [data]);
  const noResults = useMemo(
    () => hasQuery && !isLoading && items.length === 0,
    [hasQuery, isLoading, items]
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className='flex flex-1 items-center gap-2'>
        <Search />
        <Input
          placeholder={t('shelfDetail.skuSearchPlaceholder')}
          type='text'
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </div>

      {isSearchingSkus && <Label>{t('shelfDetail.skuSearchLoading')}</Label>}

      {noResults && <Label>{t('shelfDetail.skuSearchNoResults')}</Label>}

      {items.length > 0 && (
        <div className='grid min-h-0 grid-cols-2 xl:grid-cols-3 gap-2 overflow-y-scroll'>
          {items.map((item) => (
            <SkuOverviewCard
              key={item.skuMetadataId}
              item={item}
              isConfirmed={confirmedSkuMetadataId === item.skuMetadataId}
              onSelectSku={onSelectSku}
            />
          ))}
        </div>
      )}
    </div>
  );
};
